import { can } from '@domain';
import type { Capability, DocumentKind, LineAmountType, LineItemInput, LogEntry, QueueEntry, Role } from '@domain';
import { FORMS, seedFields, seedLines } from './data/forms.js';
import type { Lang } from './i18n/index.js';
import { readLang } from './lang.js';
import type { ReportKind } from './screens/ReportsScreen.js';

/*
  One role type, shared with the API.

  The screens used to work from a narrowed 'admin' | 'staff', mapped at the
  edge, on the grounds that all they needed was which side of one line somebody
  fell on. That stopped being true when a client's own administrator gained
  real powers: the line now has three positions, not two, and a screen asking
  the old question would quietly get the wrong answer for a client_admin.

  So the screens carry the real role and ask the same can() the API asks, from
  the same file. What is shown and what is permitted cannot then disagree.
*/
export type { Role };

/** @deprecated The distinction is gone; Role is the real role everywhere now. */
export type RealRole = Role;

/**
 * Which view a real role gets.
 *
 * A client_admin runs the client's own company and is still a client: they
 * prepare and submit, they do not file into the ledger. That is the service
 * they are paying for, not a restriction on them.
 */
/**
 * The role the screen should behave as.
 *
 * The real one, unless somebody is previewing what a client sees — in which
 * case the weakest role, because the point of the preview is to find things
 * that should not be on screen.
 *
 * This decides what is *shown*. What is *allowed* is decided by the API, with
 * the same function from the same file, because a control hidden in a browser
 * is decoration.
 */
export function viewRoleFor(real: RealRole | undefined, previewAsClient: boolean): RealRole {
  if (previewAsClient) return 'client_user';
  return real ?? 'client_user';
}

export interface DocumentState {
  fields: Record<string, string | boolean>;
  lines: LineItemInput[];
}

export interface GlanceTarget {
  name: string;
  acct: string;
}

export interface AppState {
  /** "dash" | "queue" | "log" | "conns" | "xero" | "list:<key>" | "form:<kind>" */
  screen: string;
  lang: Lang;
  /**
   * A CFOAI person looking at the interface as a client sees it.
   *
   * This replaces a toggle that used to set the role outright — which granted
   * nothing, because the server never consulted it, and misled everyone,
   * because it looked like it did. This changes only what is drawn.
   */
  previewAsClient: boolean;

  collapsed: boolean;
  saved: boolean;
  showPayload: boolean;
  openGroups: Record<string, boolean>;
  listView: Record<string, string>;
  docs: Record<DocumentKind, DocumentState>;
  queue: QueueEntry[];
  log: LogEntry[];
  selected: Record<string, boolean>;
  /** Per-row status read back from Xero, keyed "<list>:<rowId>". */
  reads: Record<string, string>;
  glance: GlanceTarget | null;
  csvOpen: boolean;
  csvText: string;
  pushed: number | null;
  /**
   * Where to go back to after finishing a side trip, and which field to fill
   * in when we get there.
   *
   * Set when someone needs a contact that does not exist yet: they leave the
   * invoice, create the contact, and must land back on the invoice with it
   * chosen. Without this they arrive back at an empty form having lost
   * everything they had typed.
   */
  returnTo: { screen: string; kind: DocumentKind; field: string } | null;

  /**
   * Why the last push did not go through.
   *
   * Pressing a button and having nothing happen is the worst answer an
   * interface can give. A rejection from Xero has to be visible, and it has to
   * survive until the person changes something.
   */
  pushError: string[] | null;

  /**
   * Where the back button goes from a document opened out of its usual list.
   *
   * An invoice reached from a training cohort should return to that cohort,
   * not to the list of every invoice. Null means the ordinary list.
   */
  docBackTo: string | null;

  /** Our own internal organisation id. Never a Xero tenant id. */
  orgId: number;
  orgOpen: boolean;
}

const DOCUMENT_KINDS: DocumentKind[] = ['invoice', 'bill', 'quote', 'po', 'contact', 'item'];

/*
  The detail pair is in here but not in the sidebar. They are reachable
  screens — a name on an aged summary opens one — and this list is what
  decides whether a screen key is a report at all, so leaving them out
  would make the drill-down a key nothing recognises.
*/
const REPORT_KINDS: ReportKind[] = [
  'pl',
  'balance',
  'receivables',
  'payables',
  'receivables-detail',
  'payables-detail',
];

function seedDocs(): Record<DocumentKind, DocumentState> {
  const docs = {} as Record<DocumentKind, DocumentState>;
  for (const kind of DOCUMENT_KINDS) {
    docs[kind] = { fields: seedFields(kind), lines: seedLines(kind) };
  }
  return docs;
}

export function initialState(queue: QueueEntry[], log: LogEntry[]): AppState {
  return {
    screen: 'dash',
    lang: readLang(),
    previewAsClient: false,
    /*
      Approved, not draft.

      This product is the client's interface to their own books. Filing a
      draft and then requiring somebody to open Xero and approve it makes
      Xero a step in the ordinary day, which is the thing this exists to
      remove — and it is how invoices end up sitting unapproved for a week
      because the person who files them does not have a Xero login.

      The person pressing the button holds 'submit', which is the decision.
      Draft is still one tap away for anything that genuinely wants a second
      pair of eyes.
    */
    collapsed: false,
    saved: false,
    showPayload: true,
    openGroups: { MAIN: true, PURCHASES: false, SALES: true, TRAINING: true, REPORTS: true, ADMIN: false },
    listView: {},
    docs: seedDocs(),
    queue,
    log,
    selected: {},
    reads: {},
    glance: null,
    csvOpen: false,
    csvText: '',
    pushed: null,
    returnTo: null,
    pushError: null,
    docBackTo: null,
    orgId: 1,
    orgOpen: false,
  };
}

export type Action =
  | { type: 'hydrate'; queue: QueueEntry[]; log: LogEntry[] }
  | { type: 'go'; screen: string; back?: string }
  | { type: 'goAndReturn'; screen: string; back: { screen: string; kind: DocumentKind; field: string } }
  | { type: 'clearReturn' }
  | { type: 'pushFailed'; messages: string[] }
  | { type: 'setLang'; lang: Lang }
  | { type: 'togglePreviewAsClient' }
  | { type: 'toggleCollapse' }
  | { type: 'toggleGroup'; key: string }
  | { type: 'setListView'; list: string; view: string }
  | { type: 'setField'; kind: DocumentKind; key: string; value: string | boolean }
  | { type: 'setLine'; kind: DocumentKind; index: number; key: keyof LineItemInput; value: string }
  | { type: 'addLine'; kind: DocumentKind }
  | { type: 'removeLine'; kind: DocumentKind; index: number }
  | { type: 'appendLines'; kind: DocumentKind; lines: LineItemInput[] }
  | { type: 'resetForm'; kind: DocumentKind; amountsAre?: LineAmountType }
  | { type: 'markSaved' }
  | { type: 'togglePayload' }
  | { type: 'setOrg'; orgId: number; amountsAre?: LineAmountType }
  | { type: 'toggleOrgMenu' }
  | { type: 'openGlance'; target: GlanceTarget }
  | { type: 'closeGlance' }
  | { type: 'setRead'; key: string; status: string }
  | { type: 'toggleSelected'; id: string }
  | { type: 'removeFromQueue'; id: string }
  | { type: 'enqueue'; entry: QueueEntry }
  | { type: 'pushQueue'; entries: QueueEntry[]; log: LogEntry[] }
  | { type: 'retryLog'; id: string }
  | { type: 'toggleCsv' }
  | { type: 'setCsvText'; text: string };

export function reducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'hydrate':
      return { ...state, queue: action.queue, log: action.log };

    case 'goAndReturn':
      return { ...state, screen: action.screen, returnTo: action.back, saved: false };
    case 'pushFailed':
      return { ...state, pushError: action.messages, saved: false };
    case 'clearReturn':
      return { ...state, returnTo: null };
    case 'go':
      // `back` is cleared whenever it is not given, so a return path set for
      // one document cannot still be in force three screens later.
      return { ...state, screen: action.screen, saved: false, docBackTo: action.back ?? null };

    case 'setLang':
      return { ...state, lang: action.lang };

    case 'togglePreviewAsClient':
      // Leaving the preview must also leave any screen it was hiding, or an
      // admin returns to a blank area where a screen used to be.
      return { ...state, previewAsClient: !state.previewAsClient, screen: 'dash', orgOpen: false };

    case 'toggleCollapse':
      return { ...state, collapsed: !state.collapsed };

    case 'toggleGroup':
      return {
        ...state,
        openGroups: { ...state.openGroups, [action.key]: !state.openGroups[action.key] },
      };

    case 'setListView':
      return { ...state, listView: { ...state.listView, [action.list]: action.view } };

    case 'setField': {
      // A rejection describes the document as it was. Changing it makes the
      // complaint stale, and a stale complaint is worse than none.
      const doc = state.docs[action.kind];
      return {
        ...state,
        saved: false,
        pushError: null,
        docs: {
          ...state.docs,
          [action.kind]: { ...doc, fields: { ...doc.fields, [action.key]: action.value } },
        },
      };
    }

    case 'setLine': {
      const doc = state.docs[action.kind];
      const lines = doc.lines.map((line, i) =>
        i === action.index ? { ...line, [action.key]: action.value } : line,
      );
      return { ...state, saved: false, docs: { ...state.docs, [action.kind]: { ...doc, lines } } };
    }

    case 'addLine': {
      const doc = state.docs[action.kind];
      const sales = FORMS[action.kind].discount;
      const blank: LineItemInput = {
        itemCode: '',
        description: '',
        qty: '1',
        price: '0',
        disc: '',
        accountCode: sales ? '200' : '310',
        taxType: sales ? 'OUTPUT' : 'INPUT',
      };
      return {
        ...state,
        saved: false,
        docs: { ...state.docs, [action.kind]: { ...doc, lines: [...doc.lines, blank] } },
      };
    }

    case 'removeLine': {
      const doc = state.docs[action.kind];
      return {
        ...state,
        saved: false,
        docs: {
          ...state.docs,
          [action.kind]: { ...doc, lines: doc.lines.filter((_, i) => i !== action.index) },
        },
      };
    }

    case 'appendLines': {
      const doc = state.docs[action.kind];
      return {
        ...state,
        saved: false,
        csvOpen: false,
        csvText: '',
        docs: {
          ...state.docs,
          [action.kind]: { ...doc, lines: [...doc.lines, ...action.lines] },
        },
      };
    }

    case 'resetForm':
      return {
        ...state,
        pushError: null,
        saved: false,
        docs: {
          ...state.docs,
          [action.kind]: {
            fields: {
              ...seedFields(action.kind),
              // The company's own default, where it has one. Without this
              // a cleared form goes back to Xero's 'Exclusive' and the
              // next document is stated on a basis nobody chose.
              ...(action.amountsAre ? { amountsAre: action.amountsAre } : {}),
            },
            lines: seedLines(action.kind),
          },
        },
      };

    case 'markSaved':
      return { ...state, saved: true };

    case 'togglePayload':
      return { ...state, showPayload: !state.showPayload };

    case 'setOrg': {
      /*
        How amounts are stated belongs to the company, so changing company
        changes it on every form. Anything half-typed was being prepared
        for the company you just left; carrying its tax basis across is
        how a document comes to be stated on the wrong one.
      */
      let docs = state.docs;
      const amountsAre = action.amountsAre;
      if (amountsAre) {
        docs = { ...state.docs };
        for (const kind of DOCUMENT_KINDS) {
          const doc = docs[kind];
          docs[kind] = { ...doc, fields: { ...doc.fields, amountsAre } };
        }
      }

      return { ...state, orgId: action.orgId, orgOpen: false, saved: false, glance: null, docs };
    }

    case 'toggleOrgMenu':
      return { ...state, orgOpen: !state.orgOpen };

    case 'openGlance':
      return { ...state, glance: action.target };

    case 'closeGlance':
      return { ...state, glance: null };

    case 'setRead':
      return { ...state, reads: { ...state.reads, [action.key]: action.status } };

    case 'toggleSelected':
      return { ...state, selected: { ...state.selected, [action.id]: !state.selected[action.id] } };

    case 'removeFromQueue':
      return { ...state, queue: state.queue.filter((q) => q.id !== action.id) };

    case 'enqueue':
      return { ...state, queue: [...state.queue, action.entry], screen: 'queue' };

    case 'pushQueue': {
      const pushedIds = new Set(action.entries.map((e) => e.id));
      return {
        ...state,
        queue: state.queue.filter((q) => !pushedIds.has(q.id)),
        selected: {},
        log: [...action.log, ...state.log],
        pushed: action.entries.length,
      };
    }

    case 'retryLog':
      return {
        ...state,
        log: state.log.map((entry) =>
          entry.id === action.id
            ? {
                ...entry,
                http: '200',
                ok: true,
                msg: 'Retried with the same Idempotency-Key — Created, Status DRAFT',
              }
            : entry,
        ),
      };

    case 'toggleCsv':
      return { ...state, csvOpen: !state.csvOpen };

    case 'setCsvText':
      return { ...state, csvText: action.text };
  }
}

/** The screen the current role is actually allowed to see. */
/**
 * The screen this view is allowed to show.
 *
 * Hiding a screen here is courtesy, not protection: the server refuses these
 * to anyone who is not CFOAI staff, whatever the browser decides to draw.
 */
/**
 * What a screen needs before it will show at all.
 *
 * Reached by any route other than the menu — a remembered screen, a link, a
 * reload. The API refuses independently of this, which is what actually keeps
 * anybody out; this only stops somebody landing on a page of refusals.
 */
/*
  What each screen needs, for anyone who arrives at it by any route.

  THIS IS THE SECOND COPY. The navigation carries the same requirement on
  each item in data/forms.ts, and the two must agree: this one decides
  whether the screen opens, that one decides whether the link is drawn. When
  they disagree the link appears and clicking it silently returns you to the
  dashboard, which is what happened when People was opened to organisation
  admins here and not there.

  Neither is the security boundary. The API checks every request against the
  company it names, and would refuse regardless of what either table says.

  Entries without a navigation item — 'import', reached from the contacts
  screen — are why this is not simply derived from that list.
*/
const SCREEN_NEEDS: Record<string, Capability> = {
  queue: 'submit',
  // Settling a document reaches as far as raising one. See functions/payments.ts.
  payment: 'submit',
  branding: 'operate',
  methods: 'operate',
  // Fifty contacts at once is the same act as one at a time, and adding
  // one is already offered on the invoice form. See functions/contactImport.ts.
  import: 'submit',
  // An organisation's own admin manages their own colleagues. What they
  // cannot do is appoint anyone into the practice or remove an accountant,
  // and both of those are refused by the API rather than by this table.
  people: 'operate',
  // A company's own record of what reached Xero. See functions/log.ts.
  log: 'operate',
  /*
    Running the training centre is the client's own job. A coordinator who
    can raise an invoice can certainly add a student to a cohort, and making
    them ask us would recreate the waiting this product exists to remove.
    Reading needs nothing beyond reaching the company at all.
  */
  training: 'submit',
  xero: 'administer',
  conns: 'administer',
};

export function effectiveScreen(state: AppState, role: Role): string {
  const base = state.screen.split(':')[0] ?? state.screen;
  const needed = SCREEN_NEEDS[base];
  if (needed && !can(role, needed)) return 'dash';
  return state.screen;
}

export function listKeyOf(screen: string): string | null {
  return screen.startsWith('list:') ? screen.slice(5) : null;
}

/**
 * Which report a screen key names, if it names one.
 *
 * Every report needs only `read`, so there is no entry in SCREEN_NEEDS above
 * and no `needs` on the navigation items: anyone who may reach a company may
 * read its own books. That is the whole point of the product, and it matches
 * the dashboard and the sales-and-purchases summary, which already show
 * figures at the same reach.
 */
export function reportKindOf(screen: string): ReportKind | null {
  if (!screen.startsWith('report:')) return null;
  /*
    A detail report carries the contact it is about in the key —
    'report:receivables-detail:<contactId>' — so that a reload lands back on
    the name somebody was reading rather than on an empty screen. Same
    arrangement as 'training:delivery:<id>' below, and for the same reason.
  */
  const kind = screen.slice(7).split(':')[0] ?? '';
  return REPORT_KINDS.includes(kind as ReportKind) ? (kind as ReportKind) : null;
}

/** The contact a detail report is about, or null on a report without one. */
export function reportContactOf(screen: string): string | null {
  if (!screen.startsWith('report:')) return null;
  const [, contactId] = screen.slice(7).split(':');
  return contactId && contactId.trim() !== '' ? contactId : null;
}

/**
 * Which training screen a key names.
 *
 * 'training:courses', 'training:deliveries', or 'training:delivery:<id>'.
 * One base word so SCREEN_NEEDS has one entry, and the delivery id travels
 * in the screen key rather than in state of its own — a reload then lands
 * back on the cohort somebody was looking at.
 */
export type TrainingView =
  | { view: 'courses' }
  | { view: 'deliveries' }
  /** Which accounts carry a course cost, and what has fallen out. */
  | { view: 'setup' }
  | { view: 'delivery'; deliveryId: number };

export function trainingViewOf(screen: string): TrainingView | null {
  if (!screen.startsWith('training:')) return null;
  const rest = screen.slice('training:'.length);

  if (rest === 'courses') return { view: 'courses' };
  if (rest === 'setup') return { view: 'setup' };
  if (rest === 'deliveries') return { view: 'deliveries' };

  if (rest.startsWith('delivery:')) {
    const deliveryId = Number(rest.slice('delivery:'.length));
    if (Number.isSafeInteger(deliveryId) && deliveryId > 0) return { view: 'delivery', deliveryId };
  }
  return null;
}

export function formKindOf(screen: string): DocumentKind | null {
  if (!screen.startsWith('form:')) return null;
  const kind = screen.slice(5) as DocumentKind;
  return DOCUMENT_KINDS.includes(kind) ? kind : null;
}

/**
 * Parse pasted spreadsheet rows into line items.
 * Tabs and commas both separate, because Excel yields tabs and CSV yields commas.
 */
export function parseCsv(text: string, sales: boolean): LineItemInput[] {
  return text
    .split('\n')
    .map((row) => row.split(/\t|,/))
    .filter((cells) => cells.length > 1 && String(cells[0] ?? '').trim())
    .map((cells) => ({
      itemCode: '',
      description: String(cells[0] ?? '').trim(),
      qty: String(cells[1] ?? '1').trim(),
      price: String(cells[2] ?? '0').trim(),
      disc: '',
      accountCode: String(cells[3] ?? (sales ? '200' : '310')).trim(),
      taxType: String(cells[4] ?? (sales ? 'OUTPUT' : 'INPUT')).trim(),
    }));
}
